import {useMutation} from '@apollo/client/react';
import { TDynamicVariable } from "@shared/types";
import { gql, useQuery } from "@shared/utils";
import { Box, useTheme } from "@mui/material";
import React, {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Ref,
} from "react";
import { useDrop } from "react-dnd";
import { v4 } from "uuid";

import IcIcon from "../../../IcIcon";
import { DVInlineProps } from "../DynamicVariableInline/types";
import ArithmeticOperators from "./ArithmeticOperators";

import { DynamicVariableBox } from "./DynamicVariableBox";
import FullscreenButton from "./FullscreenButton";
import { MenuFlags, NewItemButton } from "./NewItemButton";

export const DYNAMIC_VARIABLE_INNER = `
  _id
  title
  variableType
  variableType
  number
  operator
  categoryId
  subcategoryId
  takeoffVariable
  optionId
  subtype
  output
  unit
  text
`;

const VARIABLES = gql`
  query Dynamicvariables($ids: [String!]!) {
    dynamicvariables(ids: $ids) {
      ${DYNAMIC_VARIABLE_INNER}
    }
  }
`;
const ADD_VARIABLE = gql`
  mutation CreateDynamicVariable($input: CreateDynamicVariableInput!) {
    createDynamicVariable(input: $input) {
      ${DYNAMIC_VARIABLE_INNER}
    }
  }
`;

type CreateDynamicVariableMutationData = {
  createDynamicVariable?: { _id: string } | null;
};

export type VariablesProps = {
  onChange?: (variables: string[]) => Promise<void>;
  advancedVariable?: string | null;
  onAdvancedChange?: (variable: string) => Promise<void>;
  onAdvancedDelete?: () => Promise<void>;
  outputVariant?: "number" | "string" | "both";
  productId: string;
  parentProductId?: string;
  variables?: string[];
  readOnly?: boolean;
  label?: string;
  onVariableDelete: DVInlineProps["onVariableDelete"];
  removeDynamicVariable: DVInlineProps["removeDynamicVariable"];
  onFullscreen?: () => void;
  allowFullscreen?: boolean;
  wrapVariables?: boolean;
  isFullscreen?: boolean;
} & Partial<MenuFlags>;

export const Variables: FunctionComponent<VariablesProps> = ({
  onChange,
  variables,
  productId,
  parentProductId,
  label,
  outputVariant,
  onVariableDelete,
  removeDynamicVariable,
  onFullscreen,
  wrapVariables,
  allowFullscreen = true,
  readOnly,
  isFullscreen = false,
  ...menuFlags
}) => {
  const theme = useTheme();
  const [activeDynamicVariableId, setActiveDynamicVariableId] = useState<
    string | undefined
  >(undefined);
  outputVariant = outputVariant || "number";
  const [id] = useState(v4());
  const { data } = useQuery<{ dynamicvariables: Array<TDynamicVariable> }>(
    VARIABLES,
    {
      variables: { ids: variables },
    }
  );
  const [{ isOver }, drop] = useDrop(() => ({
    accept: [id],
    drop: (dv: { dynamicVariable: TDynamicVariable }) => {},
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));
  const [{ isOver: isOverDelete }, deleteDrop] = useDrop(
    () => ({
      accept: [id],
      drop: (dv: { dynamicVariable: TDynamicVariable }) => {
        variables &&
          onChange?.(variables.filter((v) => v !== dv.dynamicVariable._id));
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [variables]
  );
  const [addVariable] = useMutation<CreateDynamicVariableMutationData>(
    ADD_VARIABLE
  );

  const CleanVariables = useMemo(() => {
    return variables
      ?.map((v) => data?.dynamicvariables.find((d) => d._id === v))
      .filter((v) => !!v) as TDynamicVariable[];
  }, [data?.dynamicvariables, variables]);

  const handleAddVariable = useCallback(
    async (variable: TDynamicVariable) => {
      const { data } = await addVariable({ variables: { input: variable } });

      setActiveDynamicVariableId(data?.createDynamicVariable?._id);

      if (data?.createDynamicVariable && onChange) {
        await onChange([...(variables || []), data.createDynamicVariable._id]);
      }
    },
    [onChange, JSON.stringify(variables)]
  );

  const handleResort = useCallback(
    (id: string, toIndex: number) => {
      const currentIndex = variables?.indexOf(id);
      if (currentIndex === -1 || currentIndex === undefined || !variables) {
        return;
      }
      const newVars = [...variables.filter((i) => i !== id)];

      newVars.splice(toIndex, 0, id);
      if (onChange) {
        onChange(newVars);
      }
    },
    [JSON.stringify(variables)]
  );

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          padding: "16px 14px",
          minWidth: "80px",
          border: "1px solid #e0e0e0",
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:hover": {
            borderColor: "#2196f3",
          },
          "&:focus-within": {
            borderColor: "#1a3a5c",
            boxShadow: "0 0 0 3px rgba(33, 150, 243, 0.1)",
          },
        }}
      >
        <Box
          ref={drop as unknown as Ref<HTMLDivElement>}
          sx={{
            backgroundColor: "transparent",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            verticalAlign: "middle",
          }}
        >
          <Box
            sx={{
              display: "flex",
              overflow: "hidden",
              flexWrap: wrapVariables ? "wrap" : undefined,
              gap: 2,
            }}
          >
            {CleanVariables.map((dynamicVariable, index) => {
              return (
                <Fragment key={`${dynamicVariable._id}-${index}`}>
                  <DynamicVariableBox
                    dragAreaId={id}
                    onDrop={(dv) => {
                      handleResort(dv._id || "", index);
                    }}
                    activeDynamicVariableId={activeDynamicVariableId}
                    setActiveDynamicVariableId={setActiveDynamicVariableId}
                    readOnly={readOnly}
                    dynamicVariable={dynamicVariable}
                    productId={productId}
                    parentProductId={parentProductId}
                    onVariableDelete={onVariableDelete}
                    removeDynamicVariable={removeDynamicVariable}
                    isFullscreen={isFullscreen}
                    onChange={onChange}
                    variables={variables}
                  />
                </Fragment>
              );
            })}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {!readOnly && !isFullscreen ? (
              <Box sx={{ verticalAlign: "middle" }}>
                <NewItemButton
                  {...menuFlags}
                  enableParentProduct={!!parentProductId}
                  outputVariant={outputVariant}
                  addVariable={(v) => {
                    handleAddVariable(v);
                  }}
                />
              </Box>
            ) : null}
            {allowFullscreen ? (
              <Box sx={{ verticalAlign: "middle" }}>
                <FullscreenButton onClick={onFullscreen} />
              </Box>
            ) : null}
            <Box
              ref={deleteDrop as unknown as Ref<HTMLDivElement>}
              sx={{
                verticalAlign: "middle",
                display: isOver ? "block" : "none",
                background: isOverDelete ? "red" : "pink",
                width: "100px",
                height: "30px",
                borderRadius: "3px",
                textAlign: "center",
                paddingTop: "4px",
              }}
            >
              <IcIcon icon="mdi:trash-can-outline" />
            </Box>
          </Box>
        </Box>
        {label && (
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: "-10px",
              left: "12px",
              backgroundColor: "#ffffff",
              padding: "0 6px",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "#6c757d",
              letterSpacing: "0.02em",
            }}
          >
            {label}
          </Box>
        )}
      </Box>
      {isFullscreen && outputVariant !== "string" ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0,
            p: 1,
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          <ArithmeticOperators
            onVariableSelect={(variable) => handleAddVariable(variable)}
            addButton={
              !readOnly && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box component="span" sx={{ fontSize: 11, color: "#6c757d", fontWeight: 500, textTransform: "uppercase", mr: 1 }}>
                    Add
                  </Box>
                  <NewItemButton
                    {...menuFlags}
                    enableParentProduct={!!parentProductId}
                    outputVariant={outputVariant}
                    addVariable={(v) => {
                      handleAddVariable(v);
                    }}
                  />
                </Box>
              )
            }
          />
        </Box>
      ) : null}
    </Box>
  );
};
